import {
    CorporationPermissionUpdateRequest,
    CorporationUserRole,
} from '@kcftech/prometheustypescriptapiclient';
import { SelectOption } from 'components/forms/Select';
import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { sdClient } from 'contexts/SDKClientContext';
import { CorporationSpecificUserCellProps } from 'pages/Settings/Users/UsersTable';
import { InputHTMLAttributes, useMemo } from 'react';

const options = [
    { title: 'Admin', value: CorporationUserRole.Admin },
    { title: 'User', value: CorporationUserRole.User },
    { title: 'None', value: CorporationUserRole.None },
];

export function CorporationUserRoleCell({
    corporationId,
    rowData,
    onChange,
    disabled,
}: CorporationSpecificUserCellProps &
    Pick<InputHTMLAttributes<HTMLInputElement>, 'disabled'>) {
    const applicableRoleOptions = useMemo(() => {
        const canAssignCorporationNone = rowData.Locations.some(
            (l) => l.LocationCorporationId === corporationId
        );
        return canAssignCorporationNone
            ? options
            : options.filter((o) => o.value !== CorporationUserRole.None);
    }, [corporationId, rowData.Locations]);

    const selectedOption = useMemo(() => {
        const corporationRole = rowData.Corporations.find(
            ({ CorporationId }) => CorporationId === corporationId
        )?.CorporationUserRole;
        return options.find((role) => role.value === corporationRole);
    }, [corporationId, rowData.Corporations]);

    const onSave = async (newRole: SelectOption | undefined) => {
        if (!newRole) {
            throw new Error('Attempted to save role without a role');
        }
        try {
            await sdClient.updateCorporationPermission({
                userId: rowData.Id,
                corporationId,
                body: new CorporationPermissionUpdateRequest({
                    CorporationUserRole: newRole.value as CorporationUserRole,
                }),
            });

            onChange();
        } catch {
            return 'Unable to save application role code';
        }
        return undefined;
    };

    return (
        <DropdownInlineEditor
            options={applicableRoleOptions}
            dropdownConfig={{
                renderLabel: ({ value }) => value?.title,
                dataKey: (item) => item.value,
                renderItem: ({ item }) => item.title,
                popup: { width: '16rem' },
            }}
            value={selectedOption}
            renderValue={(value) => value?.title}
            onConfirm={onSave}
            isDisabled={disabled}
        />
    );
}
